// src/hooks/useMorningWorkout.js
// The "Redemption detected" flow: after a bender ends, look for a workout
// imported from Apple Health / Health Connect in the morning-after window and
// offer to pair it with last night. Suggest + confirm, never silent.
//
// Data stays on-device: the last session and any paired Rap Sheet live in
// localStorage only. Nothing about the workout is uploaded.
import { useState, useEffect, useCallback } from 'react';
import { computeRedemption } from '../utils/redemption';
import { isHealthSupported, ensureHealthPermission, queryWorkouts } from '../native/health';

const LAST_SESSION_KEY = 'bender_last_session';
export const RAP_SHEET_KEY = 'bender_rap_sheet';
// Opt-in: the morning redemption flow only runs if the user turned it on
// (Dashboard toggle). Off by default — drinkers who never work out shouldn't
// get stamped "In Debt" every morning.
export const RAP_SHEET_OPTIN_KEY = 'bender_rapsheet_optin';
export const isRapSheetEnabled = () => { try { return localStorage.getItem(RAP_SHEET_OPTIN_KEY) === '1'; } catch { return false; } };

const WINDOW_START_H = 3;   // a workout counts from 3h after last call…
const WINDOW_END_H   = 18;  // …until 18h after
const MAX_AGE_H      = 40;  // stop looking after ~a day and a half

const readJSON = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };
const writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

// Dev/web override so the whole flow can be tested in a browser:
//   ?workout=run,8.04,46.5        (type, km, minutes)
//   localStorage.bq_dev_workout = '{"type":"run","distanceKm":8.04,"durationMin":46.5}'
function readDevWorkout() {
  if (process.env.NODE_ENV !== 'development') return null;
  try {
    const p = new URLSearchParams(window.location.search).get('workout');
    if (p) {
      const [type, km, min] = p.split(',');
      const distanceKm = Number(km) || 0, durationMin = Number(min) || 0;
      return normalizeDev({ type, distanceKm, durationMin });
    }
    const raw = localStorage.getItem('bq_dev_workout');
    if (raw) return normalizeDev(JSON.parse(raw));
  } catch { /* ignore */ }
  return null;
}
function normalizeDev(w) {
  const distanceKm = w.distanceKm || 0, durationMin = w.durationMin || 0;
  return {
    id: 'dev-' + Date.now(),
    type: w.type || 'run',
    startMs: Date.now() - durationMin * 60000,
    endMs: Date.now(),
    distanceKm, durationMin,
    paceSecPerKm: distanceKm > 0 ? (durationMin * 60) / distanceKm : null,
    calories: w.calories || null,
    avgHr: w.avgHr || null,
    hasRoute: !!w.hasRoute,
    source: 'Dev override',
  };
}

const useMorningWorkout = () => {
  const [lastSession, setLastSession] = useState(() => readJSON(LAST_SESSION_KEY));
  const [rapSheet, setRapSheet] = useState(() => readJSON(RAP_SHEET_KEY));
  const [workout, setWorkout] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | checking | found | none | too_early | unsupported

  const check = useCallback(async () => {
    if (!isRapSheetEnabled()) { setStatus('idle'); return; } // opt-in only
    const session = readJSON(LAST_SESSION_KEY);
    setLastSession(session);
    if (!session || !session.endTime || session.dismissed) { setStatus('idle'); return; }
    // Zero drinks → zero debt → nothing to redeem. Don't push a rap sheet over
    // a walk (sessions are saved for distance too, e.g. a dry night out).
    if (!(session.drinks > 0)) { setStatus('idle'); return; }
    // Already paired this session → nothing to prompt.
    const sheet = readJSON(RAP_SHEET_KEY);
    if (sheet && sheet.session?.endTime === session.endTime) { setStatus('idle'); return; }

    const now = Date.now();
    const ageH = (now - session.endTime) / 3600000;
    if (ageH > MAX_AGE_H) { setStatus('idle'); return; }
    if (ageH < WINDOW_START_H) { setStatus('too_early'); return; }

    const dev = readDevWorkout();
    if (dev) { setWorkout(dev); setStatus('found'); return; }

    if (!isHealthSupported()) { setStatus('unsupported'); return; }
    setStatus('checking');
    try {
      const ok = await ensureHealthPermission();
      if (!ok) { setStatus('none'); return; }
      const startMs = session.endTime + WINDOW_START_H * 3600000;
      const endMs = Math.min(now, session.endTime + WINDOW_END_H * 3600000);
      const list = await queryWorkouts({ startMs, endMs });
      // Pick the most substantial workout in the window.
      const best = (list || [])
        .filter(w => (w.durationMin || 0) >= 8)
        .sort((a, b) => (b.distanceKm || 0) - (a.distanceKm || 0) || (b.durationMin || 0) - (a.durationMin || 0))[0];
      if (best) { setWorkout(best); setStatus('found'); }
      else setStatus('none');
    } catch (e) {
      console.warn('Morning workout check failed', e);
      setStatus('none');
    }
  }, []);

  // Check on mount and whenever the app comes back to the foreground.
  useEffect(() => {
    check();
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [check]);

  const pair = useCallback(() => {
    const session = readJSON(LAST_SESSION_KEY);
    if (!session || !workout) return null;
    const redemption = computeRedemption(session, workout);
    const sheet = { session, workout, redemption, pairedAt: Date.now() };
    writeJSON(RAP_SHEET_KEY, sheet);
    setRapSheet(sheet);
    setStatus('idle');
    return sheet;
  }, [workout]);

  const dismiss = useCallback(() => {
    const session = readJSON(LAST_SESSION_KEY);
    if (session) writeJSON(LAST_SESSION_KEY, { ...session, dismissed: true });
    setStatus('idle');
  }, []);

  // "No workout found" is a feature (the IN DEBT verdict) — let the user own it.
  const pairNothing = useCallback(() => {
    const session = readJSON(LAST_SESSION_KEY);
    if (!session) return null;
    const redemption = computeRedemption(session, null);
    const sheet = { session, workout: null, redemption, pairedAt: Date.now() };
    writeJSON(RAP_SHEET_KEY, sheet);
    setRapSheet(sheet);
    setStatus('idle');
    return sheet;
  }, []);

  // Manual entry — works on every platform (web/Android today, and iOS when
  // HealthKit has nothing). The user types what Strava/Garmin told them; we
  // still never record a workout ourselves.
  const logManual = useCallback(({ type, distanceKm, durationMin }) => {
    const session = readJSON(LAST_SESSION_KEY);
    if (!session) return null;
    const km = Math.max(0, Number(distanceKm) || 0);
    const min = Math.max(0, Number(durationMin) || 0);
    const w = {
      id: 'manual-' + Date.now(),
      type: type || 'default',
      startMs: Date.now() - min * 60000,
      endMs: Date.now(),
      distanceKm: km,
      durationMin: min,
      paceSecPerKm: km > 0.2 && min > 0 ? (min * 60) / km : null,
      calories: null, avgHr: null, hasRoute: false,
      source: 'Logged manually',
    };
    const redemption = computeRedemption(session, w);
    const sheet = { session, workout: w, redemption, pairedAt: Date.now() };
    writeJSON(RAP_SHEET_KEY, sheet);
    setRapSheet(sheet);
    setStatus('idle');
    return sheet;
  }, []);

  // Is there an un-paired session inside the redemption window? (drives the
  // manual "Log your redemption" card on platforms with no health data)
  const canLog = ['found', 'none', 'unsupported', 'checking'].includes(status);

  return { lastSession, workout, status, rapSheet, canLog, pair, pairNothing, logManual, dismiss, recheck: check };
};

export default useMorningWorkout;
